import { ReactNode, useCallback, useEffect, useState } from "react";
import { IconAlertCircle, IconClock, IconCoin, IconHourglass } from "@tabler/icons-react";
import { axiosInstance } from "../../utils/axiosInstance";
import PageContainer from "../layouts/PageContainer";
import {
  Card,
  EmptyState,
  ErrorBanner,
  LoadingPanel,
  PageHeader,
  RefreshButton,
  Stat,
} from "../ui";
import ReportsDateFilter from "./ReportsDateFilter";
import ReportsSubNav from "./ReportsSubNav";
import { ReportsOverviewPayload, WorstOffender } from "./reportsTypes";

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split("T")[0];
}

function formatMoney(n: number): string {
  return `£${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function WorstTile({
  label,
  offender,
  format,
  to,
  icon,
}: {
  label: string;
  offender: WorstOffender | null;
  format: (v: number) => string;
  to: string;
  icon: ReactNode;
}) {
  if (!offender) {
    return <Stat icon={icon} label={label} value="—" />;
  }
  return (
    <Stat
      to={to}
      icon={icon}
      label={label}
      value={format(offender.value)}
      note={`${offender.name}${offender.locationPin ? ` (${offender.locationPin})` : ""}`}
    />
  );
}

export default function ReportsOverview() {
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(daysAgo(0));
  const [data, setData] = useState<ReportsOverviewPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (f: string, t: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get<ReportsOverviewPayload>("/reports/overview", {
        params: { from: f, to: t },
      });
      setData(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to load reports overview.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(from, to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyRange = (f: string, t: string) => {
    setFrom(f);
    setTo(t);
    load(f, t);
  };

  return (
    <PageContainer>
      <PageHeader
        title="Reports"
        subtitle={data?.generatedAt ? `${data.range.from} to ${data.range.to}` : undefined}
        actions={<RefreshButton onClick={() => load(from, to)} loading={loading} />}
      />

      <div className="sq-stack">
        <ReportsSubNav />
        <ReportsDateFilter from={from} to={to} onApply={applyRange} loading={loading} />

        {error ? <ErrorBanner onRetry={() => load(from, to)}>{error}</ErrorBanner> : null}
        {loading && !data ? (
          <Card>
            <LoadingPanel label="Loading report overview" />
          </Card>
        ) : null}

        {data ? (
          <>
            <section aria-label="Worst performers" className="sq-stats">
              <WorstTile
                icon={<IconAlertCircle size={16} />}
                label="Most errors"
                offender={data.worst.mostErrors}
                format={(v) => `${v} errors`}
                to="/reports/errors"
              />
              <WorstTile
                icon={<IconClock size={16} />}
                label="Most downtime"
                offender={data.worst.mostDowntime}
                format={(v) => `${v}h down`}
                to="/reports/downtime"
              />
              <WorstTile
                icon={<IconHourglass size={16} />}
                label="Slowest to clear"
                offender={data.worst.slowestToClear}
                format={(v) => `${v} min avg`}
                to="/reports/machines-left-complete"
              />
              <WorstTile
                icon={<IconCoin size={16} />}
                label="Lowest sales"
                offender={data.worst.lowestSales}
                format={(v) => formatMoney(v)}
                to="/reports/sales-by-store"
              />
            </section>

            <Card
              flush
              title="Store scorecard"
              subtitle="Every location, side by side. Worst value in each column is highlighted."
              aria-label="Store scorecard"
            >
              {data.scorecard.length === 0 ? (
                <EmptyState title="No locations configured" />
              ) : (
                <div className="sq-table-wrap">
                  <table className="sq-table is-stacked cols-4">
                    <thead>
                      <tr>
                        <th scope="col">Store</th>
                        <th scope="col" className="num">Sales</th>
                        <th scope="col" className="num">Errors</th>
                        <th scope="col" className="num">Downtime (h)</th>
                        <th scope="col" className="num">Left complete (avg min)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.scorecard.map((row) => (
                        <tr key={row.locationId}>
                          <td className="cell-title">
                            <div className="sq-strong">{row.name}</div>
                            {row.locationPin ? (
                              <div className="sq-muted">{row.locationPin}</div>
                            ) : null}
                          </td>
                          <td
                            className={`num${
                              row.locationId === data.worst.lowestSales?.locationId
                                ? " sq-value-bad"
                                : ""
                            }`}
                            data-label="Sales"
                          >
                            {formatMoney(row.totalSales)}
                          </td>
                          <td
                            className={`num${
                              row.locationId === data.worst.mostErrors?.locationId
                                ? " sq-value-bad"
                                : ""
                            }`}
                            data-label="Errors"
                          >
                            {row.errorCount}
                          </td>
                          <td
                            className={`num${
                              row.locationId === data.worst.mostDowntime?.locationId
                                ? " sq-value-bad"
                                : ""
                            }`}
                            data-label="Down (h)"
                          >
                            {row.downtimeHours}
                          </td>
                          <td
                            className={`num${
                              row.locationId === data.worst.slowestToClear?.locationId
                                ? " sq-value-bad"
                                : ""
                            }`}
                            data-label="Left (min)"
                          >
                            {row.avgCompleteMinutes || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        ) : null}
      </div>
    </PageContainer>
  );
}
